// Usage bands, kept in their own module so a view that only needs a label or a
// band lookup does not transitively import reckonerTable.ts. index.ts imports
// the 43KB generated table at module scope, so anything reaching for these
// constants through the barrel would pull that table into the same bundle
// chunk. index.ts re-exports everything here, so existing import sites are
// unaffected.

export type Band = "high" | "med" | "low"

// Usage band boundaries. The source range column reads "<2800 / 2801 to 4499
// / >4500"; 2800 and 4500 are unassigned there and fall to low and high.
export const BAND_LOW_MAX_KWH = 2800
export const BAND_HIGH_MIN_KWH = 4500

// Representative annual consumption the reckoner table was modelled on. The
// usage step commits exactly these figures so the customer's answer and the
// estimate share one assumption, and every band stays reachable.
export const BAND_USAGE_KWH: Record<Band, number> = {
  low: BAND_LOW_MAX_KWH,
  med: 3600,
  high: BAND_HIGH_MIN_KWH,
}

export const BAND_TITLES: Record<Band, string> = {
  low: "Low",
  med: "Medium",
  high: "High",
}

const formatKwh = (kwh: number) => kwh.toLocaleString("en-GB")

// The band a customer self-selects against. Low/high show the boundaries
// bandForUsage actually applies; med shows its representative figure ("c.")
// since the low/high labels already delimit the middle band.
export const BAND_RANGE_LABELS: Record<Band, string> = {
  low: `up to ${formatKwh(BAND_LOW_MAX_KWH)} kWh`,
  med: `c. ${formatKwh(BAND_USAGE_KWH.med)} kWh`,
  high: `${formatKwh(BAND_HIGH_MIN_KWH)} kWh or more`,
}

// Accepts an exact number or a banded answer string ("Estimated 3600"); falls
// back to med when no usage was collected.
export function bandForUsage(raw: string | number | null): Band {
  if (raw == null) return "med"
  const kwh = Number(String(raw).replace(/[^0-9.]/g, ""))
  if (!kwh || Number.isNaN(kwh)) return "med"
  if (kwh <= BAND_LOW_MAX_KWH) return "low"
  if (kwh >= BAND_HIGH_MIN_KWH) return "high"
  return "med"
}
